import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, Save, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import * as settingsApi from '../endpoints';

const mockInvoices = [
  { id: 'INV-2024-001', date: '2024-01-15', amount: 149, status: 'paid', dueDate: '2024-01-30' },
  { id: 'INV-2023-012', date: '2023-12-15', amount: 149, status: 'paid', dueDate: '2023-12-30' },
  { id: 'INV-2023-011', date: '2023-11-15', amount: 149, status: 'paid', dueDate: '2023-11-30' },
  { id: 'INV-2023-010', date: '2023-10-15', amount: 49, status: 'paid', dueDate: '2023-10-30' },
  { id: 'INV-2023-009', date: '2023-09-15', amount: 49, status: 'paid', dueDate: '2023-09-30' },
];

export default function InvoicesTab({
  invoicingForm,
  setInvoicingForm,
  invoices,
  formatDate
}) {
  const [saving, setSaving] = useState(false);

  const handleInvoicingSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateInvoicing(invoicingForm);
      toast.success('Invoicing settings saved');
    } catch (error) {
      toast.error('Failed to save invoicing settings');
    } finally {
      setSaving(false);
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const responseData = await settingsApi.downloadInvoice(invoiceId);
      const blob = new Blob([responseData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded!');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const localFormatDate = formatDate || ((dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  });

  return (
    <div className="space-y-6">
      {/* Invoice Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Invoice Settings</CardTitle>
          <CardDescription>Configure how your invoices are generated</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invoiceEmail">Invoice Email</Label>
              <Input
                id="invoiceEmail"
                type="email"
                value={invoicingForm.invoiceEmail}
                onChange={(e) => setInvoicingForm(prev => ({ ...prev, invoiceEmail: e.target.value }))}
                placeholder="billing@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoicePrefix">Invoice Prefix</Label>
              <Input
                id="invoicePrefix"
                value={invoicingForm.invoicePrefix}
                onChange={(e) => setInvoicingForm(prev => ({ ...prev, invoicePrefix: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={invoicingForm.currency}
                onChange={(e) => setInvoicingForm(prev => ({ ...prev, currency: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div>
              <Label htmlFor="autoSend" className="font-medium">Auto-send Invoices</Label>
              <p className="text-sm text-muted-foreground">Automatically send invoices when generated</p>
            </div>
            <Switch
              id="autoSend"
              checked={invoicingForm.autoSendInvoices}
              onCheckedChange={(checked) => setInvoicingForm(prev => ({ ...prev, autoSendInvoices: checked }))}
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleInvoicingSave} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invoice History */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Invoice History</CardTitle>
          <CardDescription>Download and manage your invoices</CardDescription>
        </CardHeader>
        <CardContent>
          {(invoices.length > 0 || mockInvoices.length > 0) ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(invoices.length > 0 ? invoices : mockInvoices).map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">{invoice.id}</TableCell>
                    <TableCell>{localFormatDate(invoice.created_at || invoice.date)}</TableCell>
                    <TableCell>{localFormatDate(invoice.due_date || invoice.dueDate)}</TableCell>
                    <TableCell className="font-medium">${invoice.amount}.00</TableCell>
                    <TableCell>
                      <Badge variant={invoice.status === 'paid' ? 'default' : invoice.status === 'pending' ? 'secondary' : 'destructive'}>
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => downloadInvoice(invoice.id)} data-testid={`download-invoice-${invoice.id}`}>
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No invoices yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
